import { useState } from 'react';
import { periodsApi } from '../../api/services';
import type { ClosePreviewDto, PeriodDto } from '../../api/types';
import {
  Alert, Badge, Button, Card, Field, Form, Input, Loadable, Modal, NumberInput, PageHeader, Select, Table, fmtDate, fmtMoney, useAction, useAsync,
} from '../../ui/components';
import { PageHelp } from '../../ui/help';
import { Link } from '../../router';

const MONTHS = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Setiembre', 'Octubre', 'Noviembre', 'Diciembre'];

export function PeriodsPage() {
  const [year, setYear] = useState<number>(new Date().getFullYear());
  const periods = useAsync(() => periodsApi.list(year), [year]);
  const fiscal = useAsync(() => periodsApi.fiscalCloses(), []);
  const [creating, setCreating] = useState(false);
  const [closing, setClosing] = useState<PeriodDto | null>(null);
  const [fiscalForm, setFiscalForm] = useState(false);
  const { run, busy } = useAction();

  return (
    <>
      <PageHeader title="Períodos contables" actions={<><Button onClick={() => setFiscalForm(true)}>Registrar cierre fiscal</Button><Button kind="primary" onClick={() => setCreating(true)}>Abrir período</Button></>} />
      <PageHelp page="periods" />
      <div className="acc-toolbar">
        <Field label="Año"><NumberInput step="1" value={year} onChange={(v) => setYear(v || new Date().getFullYear())} /></Field>
      </div>
      <Loadable state={periods}>
        {(rows) => (
          <Table rows={rows} rowKey={(p) => p.date} columns={[
            { key: 'f', title: 'Fecha del período', render: (p) => fmtDate(p.date) },
            { key: 'd', title: 'Descripción', render: (p) => p.description },
            { key: 'e', title: 'Estado', render: (p) => <Badge tone={p.status === 'A' ? 'ok' : 'neutral'}>{p.status === 'A' ? 'Abierto' : 'Cerrado'}</Badge> },
            { key: 'o', title: 'Asientos abiertos', align: 'right', render: (p) => p.openEntries },
            { key: 'fc', title: 'Cierre fiscal', render: (p) => (p.hasFiscalClose ? 'Sí' : '') },
            { key: 'x', title: '', render: (p) => (p.status === 'A' ? <Button kind="link" onClick={() => setClosing(p)}>Cerrar…</Button> : null) },
          ]} />
        )}
      </Loadable>
      <Card title="Meses de cierre fiscal">
        <Loadable state={fiscal}>
          {(rows) => (
            <Table rows={rows} rowKey={(f) => f.date} columns={[
              { key: 'f', title: 'Mes', render: (f) => fmtDate(f.date) },
              { key: 'd', title: 'Detalle', render: (f) => f.detail ?? '' },
              { key: 'i', title: 'Registrado', render: (f) => fmtDate(f.inclusionDate) },
              { key: 'x', title: '', render: (f) => <Button kind="link" busy={busy} onClick={() => run(() => periodsApi.deleteFiscalClose(f.date), { confirm: 'Eliminar este mes de cierre fiscal? Sólo es posible si el período aún no se cerró.', danger: true, success: 'Cierre fiscal eliminado.' }).then(fiscal.reload)}>Eliminar</Button> },
            ]} />
          )}
        </Loadable>
      </Card>
      {creating && <CreatePeriod onClose={() => setCreating(false)} onSaved={() => { setCreating(false); periods.reload(); }} />}
      {closing && <ClosePeriod period={closing} onClose={() => setClosing(null)} onDone={() => { setClosing(null); periods.reload(); }} />}
      {fiscalForm && <FiscalForm onClose={() => setFiscalForm(false)} onSaved={() => { setFiscalForm(false); fiscal.reload(); }} />}
    </>
  );
}

function CreatePeriod({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [description, setDescription] = useState('');
  const { run, busy } = useAction();
  return (
    <Modal title="Abrir período" onClose={onClose} size="sm">
      <Form onSubmit={() => run(async () => { await periodsApi.create({ year, month, description: description.trim() || null }); onSaved(); }, { success: 'Período abierto.' })}>
        <Field label="Año"><NumberInput step="1" value={year} onChange={setYear} /></Field>
        <Field label="Mes"><Select value={month} onChange={setMonth} options={MONTHS.map((m, i) => ({ value: i + 1, label: m }))} /></Field>
        <Field label="Descripción" hint="Vacío = nombre del mes"><Input value={description} onChange={setDescription} maxLength={100} /></Field>
        <div className="acc-actions"><Button onClick={onClose}>Cancelar</Button><Button kind="primary" type="submit" busy={busy}>Abrir</Button></div>
      </Form>
    </Modal>
  );
}

function ClosePeriod({ period, onClose, onDone }: { period: PeriodDto; onClose: () => void; onDone: () => void }) {
  const preview = useAsync<ClosePreviewDto>(() => periodsApi.closePreview(period.date), [period.date]);
  const { run, busy } = useAction();
  return (
    <Modal title={`Cerrar período ${fmtDate(period.date)}`} onClose={onClose}>
      <Loadable state={preview}>
        {(p) => (
          <>
            <dl className="acc-dl">
              <dt>Asientos contabilizados</dt><dd>{p.postedEntries}</dd>
              <dt>Asientos abiertos</dt><dd>{p.openEntries}</dd>
              <dt>Débitos / créditos</dt><dd>{fmtMoney(p.totalDebit)} / {fmtMoney(p.totalCredit)}</dd>
              <dt>Cierre fiscal</dt><dd>{p.fiscalCloseWillApply ? 'Sí: se ponen en cero los acumulados de las cuentas' : 'No'}</dd>
            </dl>
            {p.blockers.map((b) => <Alert key={b} kind="error">{b}</Alert>)}
            {(p.warnings ?? []).map((w) => <Alert key={w} kind="warn">{w}</Alert>)}
            {p.blockers.some((b) => b.includes('cierre anual')) && <p><Link to="/year-end">Ir a Cierre anual</Link></p>}
            {p.canClose && <Alert kind="warn">El cierre no se puede deshacer desde el sistema: los asientos del período quedan cerrados y no admiten cambios.</Alert>}
            <div className="acc-actions">
              <Button onClick={onClose}>Cancelar</Button>
              <Button kind="danger" disabled={!p.canClose} busy={busy} onClick={() => run(async () => {
                const r = await periodsApi.close(period.date);
                onDone();
                return r;
              }, { confirm: `Confirma el cierre del período ${fmtDate(period.date)}?`, danger: true, success: 'Período cerrado.' })}>Cerrar período</Button>
            </div>
          </>
        )}
      </Loadable>
    </Modal>
  );
}

function FiscalForm({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(12);
  const [detail, setDetail] = useState('');
  const { run, busy } = useAction();
  return (
    <Modal title="Registrar mes de cierre fiscal" onClose={onClose} size="sm">
      <Form onSubmit={() => run(async () => { await periodsApi.createFiscalClose(year, month, detail.trim() || undefined); onSaved(); }, { success: 'Cierre fiscal registrado.' })}>
        <Field label="Año"><NumberInput step="1" value={year} onChange={setYear} /></Field>
        <Field label="Mes"><Select value={month} onChange={setMonth} options={MONTHS.map((m, i) => ({ value: i + 1, label: m }))} /></Field>
        <Field label="Detalle"><Input value={detail} onChange={setDetail} maxLength={200} /></Field>
        <div className="acc-actions"><Button onClick={onClose}>Cancelar</Button><Button kind="primary" type="submit" busy={busy}>Registrar</Button></div>
      </Form>
    </Modal>
  );
}
